import { useMemo, useState } from "react";
import { Ban, Loader2, Trash2 } from "lucide-react";
import { useLanguage } from "@/hooks/use-language";
import { useAddBlockedIP, useBlockedIPs, useRemoveBlockedIP } from "@/hooks/use-admin";
import { createErrorMessage } from "@/lib/error-message";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const DURATIONS = [
  { hours: "0", key: "admin_block.duration_forever" },
  { hours: "1", key: "admin_block.duration_1h" },
  { hours: "24", key: "admin_block.duration_24h" },
  { hours: String(24 * 7), key: "admin_block.duration_7d" },
  { hours: String(24 * 30), key: "admin_block.duration_30d" },
] as const;

function formatDate(iso: string, locale: string) {
  return new Date(iso).toLocaleString(locale === "ko" ? "ko-KR" : "en-US", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

// Bare /32 and /128 prefixes are shown as plain addresses.
function displayIP(prefix: string) {
  return prefix.replace(/\/(32|128)$/, "");
}

export function IPBlockPanel() {
  const { t, locale } = useLanguage();
  const { data: blocks = [], isLoading, error: listError } = useBlockedIPs();
  const addMutation = useAddBlockedIP();
  const removeMutation = useRemoveBlockedIP();

  const [ip, setIP] = useState("");
  const [reason, setReason] = useState("");
  const [hours, setHours] = useState("0");

  const durationItems = useMemo(
    () => Object.fromEntries(DURATIONS.map((d) => [d.hours, t(d.key)])),
    [t],
  );

  const error = listError ?? addMutation.error ?? removeMutation.error ?? null;

  function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!ip.trim()) return;
    addMutation.mutate(
      { ip: ip.trim(), reason: reason.trim(), hours: Number(hours) },
      {
        onSuccess: () => {
          setIP("");
          setReason("");
        },
      },
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">{t("admin_block.title")}</h2>
        <p className="text-sm text-muted-foreground">{t("admin_block.desc")}</p>
      </div>

      <Card>
        <CardContent className="pt-6">
          <form
            onSubmit={handleAdd}
            className="grid gap-3 sm:grid-cols-[minmax(0,1.2fr)_minmax(0,1.5fr)_minmax(0,0.9fr)_auto] sm:items-end"
          >
            <div className="space-y-1.5">
              <Label htmlFor="block-ip">{t("admin_block.ip_label")}</Label>
              <Input
                id="block-ip"
                value={ip}
                onChange={(e) => setIP(e.target.value)}
                placeholder={t("admin_block.ip_placeholder")}
                autoComplete="off"
                inputMode="decimal"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="block-reason">{t("admin_block.reason_label")}</Label>
              <Input
                id="block-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t("admin_block.reason_placeholder")}
                autoComplete="off"
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t("admin_block.duration_label")}</Label>
              <Select items={durationItems} value={hours} onValueChange={(v) => setHours(v ?? "0")}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DURATIONS.map((d) => (
                    <SelectItem key={d.hours} value={d.hours}>
                      {t(d.key)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              type="submit"
              variant="destructive"
              disabled={!ip.trim() || addMutation.isPending}
              className="cursor-pointer"
            >
              {addMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : <Ban className="size-4" />}
              {t("admin_block.add")}
            </Button>
          </form>
          {error && (
            <p className="mt-3 text-sm text-destructive">{createErrorMessage(error)}</p>
          )}
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
        </div>
      ) : blocks.length === 0 ? (
        <div className="rounded-lg border border-dashed py-12 text-center text-sm text-muted-foreground">
          {t("admin_block.empty")}
        </div>
      ) : (
        <div className="space-y-2">
          {blocks.map((b) => (
            <Card key={b.ip}>
              <CardContent className="flex flex-col gap-2 py-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-sm font-semibold">{displayIP(b.ip)}</span>
                    <Badge variant="secondary" className="text-[11px]">
                      {b.expires_at
                        ? `${t("admin_block.until")} ${formatDate(b.expires_at, locale)}`
                        : t("admin_block.forever")}
                    </Badge>
                  </div>
                  {b.reason && <p className="text-sm break-words">{b.reason}</p>}
                  <p className="text-xs text-muted-foreground">
                    {t("admin_block.by")}: {b.created_by} · {formatDate(b.created_at, locale)}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="shrink-0 cursor-pointer"
                  disabled={removeMutation.isPending && removeMutation.variables === b.ip}
                  onClick={() => removeMutation.mutate(b.ip)}
                >
                  <Trash2 className="size-4" />
                  {t("admin_block.remove")}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
